import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CharonView } from "./charonView.ts";
import { InfernoDetails } from "./infernoDetails.ts";
import {InfernoTerrainView} from './infernoTerrainView.ts';
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ArenaBox, ArenaDefinition, TerrainDamage } from "@arena/shared";
import { TEXELS_PER_METRE, surfaceRepeat, surfaceTexture, type SurfaceKind } from "./pixelTextures.ts";
import type { Hd2dWorld } from "./hd2d.ts";
import { segmentHitsBox } from "./geometry.ts";
import {INFERNO_LAYOUT,infernoEdge} from '@arena/arena-data';

/**
 * L'ingresso dell'Inferno drawn for the HD-2D look (D39): the plateau of
 * burnt rock with its cliffs, the Acheron flowing below the north edge,
 * Charon waiting at the end of the pier, the walls of the abyss all around
 * and the Gate with its inscription. The objects on the plateau are drawn by
 * `PropsView`; the boxes of the definition stay invisible.
 */

const SEGMENTS = 160;
/** The plateau's outline, as in `@arena/arena-data`'s inferno.ts. */
const SHAPE = INFERNO_LAYOUT;
const ASH_TILE = 64 / TEXELS_PER_METRE;

export class InfernoView {
  private readonly receivers: Mesh[] = [];
  private readonly casters: Mesh[] = [];
  private readonly torches: Vector3[] = [];
  private readonly river: Texture;
  private readonly riverGlow: StandardMaterial;
  private readonly charon: CharonView;
  private readonly details:InfernoDetails;
  private readonly terrain:InfernoTerrainView;
  /** The mountain of the Gate: it fades when the camera goes behind it. */
  private readonly massif: StandardMaterial | null = null;
  private readonly occluders: { box: ArenaBox; material: StandardMaterial }[] = [];
  private time = 0;

  constructor(scene: Scene, arena: ArenaDefinition) {
    // The plateau: burnt ground on top, rock cliffs down into the dark.
    this.terrain=new InfernoTerrainView(scene,arena);
    this.receivers.push(...this.terrain.meshes);
    const cliff = this.mesh(scene, "cliff", plateauCliff(9), "rock");
    this.receivers.push(cliff);
    this.mesh(scene,'shore-bank',shoreBank(),'rock');
    this.buildEasternBoundary(scene);
    const foothills=CreateGround('foreground-dark-foothills',{width:130,height:90},scene);
    foothills.position.set(0,-13,-25);
    foothills.material=this.textured(scene,'foothills','rock',18,12);
    (foothills.material as StandardMaterial).diffuseColor.set(.22,.19,.17);
    foothills.isPickable=false;

    const pier = arena.solids.find((b) => b.id === "pier");
    if (pier) this.buildPier(scene, pier);
    const lintel = arena.solids.find((b) => b.id === "gate-lintel");
    if (lintel) this.buildLintel(scene, lintel);
    const massif = arena.solids.filter((b) => b.id.startsWith("massif") || b.id.startsWith('mountain-'));
    if (massif.length > 0) this.massif = this.buildMassif(scene, massif);

    // The Acheron: dark water under the north edge, slowly flowing, a dull red glow on it.
    const water = CreateGround("acheron", { width: 120, height: 50, subdivisions: 1 }, scene);
    water.position.set(0, -2.4, 30);
    const riverMaterial = new StandardMaterial("acheron", scene);
    this.river = surfaceTexture(scene, "water");
    this.river.uScale = (120 / 2) * surfaceRepeat("water");
    this.river.vScale = (50 / 2) * surfaceRepeat("water");
    riverMaterial.diffuseTexture = this.river;
    riverMaterial.specularColor = Color3.Black();
    riverMaterial.emissiveColor = Color3.FromHexString("#2a0c0a");
    this.riverGlow = riverMaterial;
    water.material = riverMaterial;
    water.isPickable = false;

    // The abyss below everything else, glowing from far down.
    const abyss = CreateDisc("abyss", { radius: 70, tessellation: 48 }, scene);
    abyss.rotation.x = Math.PI / 2;
    abyss.position.y = -16;
    const abyssMaterial = new StandardMaterial("abyss", scene);
    abyssMaterial.disableLighting = true;
    abyssMaterial.emissiveColor = Color3.FromHexString("#210e09");
    abyss.material = abyssMaterial;
    abyss.isPickable = false;

    // Walls of the abyss far around: the inside of the funnel of Hell.
    const walls = CreateCylinder("abyss-walls", { height: 60, diameter: 96, tessellation: 40, sideOrientation: Mesh.BACKSIDE, cap: Mesh.NO_CAP }, scene);
    walls.position.set(0, 4, 4);
    walls.scaling.z = 0.8;
    // Tiled in metres like everything else: about 300 m around, 60 m tall.
    walls.material = this.textured(scene, "abyss-walls", "rock", 150, 30);
    // Background: dark, so the plateau and what stands on it read first.
    (walls.material as StandardMaterial).diffuseColor = new Color3(0.6, 0.5, 0.46);
    // Lit from the lava below: warm at the bottom of the funnel.
    (walls.material as StandardMaterial).emissiveColor = Color3.FromHexString("#3a1206");
    walls.isPickable = false;

    // Charon at the end of the pier, with his lantern.
    const z = pier ? pier.max.z + 1.4 : 14;
    const x = pier ? (pier.min.x + pier.max.x) / 2 : 5.75;
    // His lantern glows but casts no light: the light budget (8) goes to the braziers and the descent.
    this.charon = new CharonView(scene, x, z);
    this.details=new InfernoDetails(scene);
  }

  /** Where the braziers burn come from the objects; Charon's lantern from here. */
  updateTerrain(states:Iterable<[string,TerrainDamage]>):void {
    const values=[...states];this.terrain.update(values);this.details.updateTerrain(values);
  }
  hd2dWorld(extra: { casters: Mesh[]; torches: Vector3[] }): Hd2dWorld {
    return {
      casters: [...this.casters, ...extra.casters],
      receivers: [...this.receivers, ...extra.casters],
      torches: [...extra.torches, ...this.torches],
      extent: { x: SHAPE.a, z: SHAPE.b },
      mood: "inferno",
    };
  }

  update(dt: number, camera?: Vector3, focus: Vector3 | null = null): void {
    this.time += dt;
    if (this.massif && camera) {
      const blocked = focus !== null && this.occluders.some(({ box }) => segmentHitsBox(camera, focus, box, 0.35));
      const target = camera.x < -16.5 || blocked ? 0.15 : 1;
      this.massif.alpha += (target - this.massif.alpha) * (1 - Math.exp(-dt * 8));
    }
    for (const { box, material } of this.occluders) {
      const blocked = camera && focus && segmentHitsBox(camera, focus, box, 0.35);
      const target = blocked ? 0.15 : 1;
      material.alpha += (target - material.alpha) * (1 - Math.exp(-dt * 10));
      if (Math.abs(material.alpha - target) < 0.005) material.alpha = target;
    }
    this.river.uOffset = (this.time * 0.02) % 1;
    this.river.vOffset = (this.time * -0.05) % 1;
    const pulse = 0.16 + 0.04 * Math.sin(this.time * 0.8);
    this.riverGlow.emissiveColor.set(pulse, pulse * 0.28, pulse * 0.22);
    this.charon.update(dt);
    this.details.update(dt);
  }

  react(x:number,z:number):void {this.details.react(x,z);}

  // -------------------------------------------------------------------------

  private buildPier(scene: Scene, pier: ArenaBox): void {
    const w = pier.max.x - pier.min.x;
    const d = pier.max.z - pier.min.z;
    const deck = CreateBox("pier", { width: w, height: 0.25, depth: d }, scene);
    deck.position.set((pier.min.x + pier.max.x) / 2, -0.12, (pier.min.z + pier.max.z) / 2);
    deck.material = this.textured(scene, "pier", "wood", w / 2, d / 2);
    deck.isPickable = false;
    this.receivers.push(deck);
    for (const px of [pier.min.x + 0.15, pier.max.x - 0.15]) {
      for (let pz = pier.min.z + 1.2; pz < pier.max.z; pz += 1.8) {
        const post = CreateCylinder("pier-post", { height: 3, diameter: 0.22, tessellation: 6 }, scene);
        post.position.set(px, -1.3, pz);
        post.material = deck.material;
        post.isPickable = false;
      }
    }
  }

  private buildLintel(scene: Scene, box: ArenaBox): void {
    const w = box.max.x - box.min.x;
    const h = box.max.y - box.min.y;
    const d = box.max.z - box.min.z;
    const lintel = CreateBox("gate-lintel", { width: w, height: h, depth: d }, scene);
    lintel.position.set((box.min.x + box.max.x) / 2, (box.min.y + box.max.y) / 2, (box.min.z + box.max.z) / 2);
    lintel.material = this.textured(scene, "gate-lintel", "brick", d / 2, h / 2);
    this.occluders.push({ box, material: lintel.material as StandardMaterial });
    lintel.isPickable = false;
    this.casters.push(lintel);
    this.receivers.push(lintel);
    // "Per me si va ne la città dolente…": the last line, the one everybody knows, on the side facing the arena.
    const text = new DynamicTexture("inscription", { width: 1024, height: 192 }, scene, false, Texture.BILINEAR_SAMPLINGMODE);
    const g = text.getContext() as unknown as CanvasRenderingContext2D;
    const paintInscription=()=>{
    g.clearRect(0, 0, 1024, 192);
    g.fillStyle = "#140b09";
    g.fillRect(0, 0, 1024, 192);
    g.strokeStyle='#84603b';g.lineWidth=3;g.strokeRect(14,12,996,168);
    g.font = '64px "Pirata One", Georgia, serif';
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = "#e8bd79";
    g.fillText("Lasciate ogne speranza", 512, 70);
    g.font = '50px "Pirata One", Georgia, serif';
    g.fillText("voi ch’intrate", 512, 137);
    text.update(true);
    };
    paintInscription();
    const font=new FontFace('Pirata One','url(/fonts/PirataOne-Regular.ttf)');
    void font.load().then(loaded=>{document.fonts.add(loaded);paintInscription();}).catch(()=>{});
    const plate = CreatePlane("inscription", { width: d - 0.4, height: h - 0.25 }, scene);
    plate.position.set(box.max.x + 0.02, (box.min.y + box.max.y) / 2, (box.min.z + box.max.z) / 2);
    plate.rotation.y = -Math.PI / 2;
    const plateMaterial = new StandardMaterial("inscription", scene);
    plateMaterial.diffuseTexture = text;
    plateMaterial.emissiveTexture = text;
    plateMaterial.specularColor = Color3.Black();
    plateMaterial.backFaceCulling = false;
    plate.material = plateMaterial;
    this.occluders.push({ box, material: plateMaterial });
    plate.isPickable = false;
  }

  /**
   * The rock the Gate is cut into: blocks with a ragged top of boulders, and
   * beyond the arch a passage going down into a red glow.
   */
  private buildMassif(scene: Scene, boxes: ArenaBox[]): StandardMaterial {
    const material = this.textured(scene, "massif", "rock", 4, 4);
    let seed = 7;
    const random = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    for (const box of boxes) {
      const w = box.max.x - box.min.x;
      const h = Math.min(box.max.y, 12) - Math.max(box.min.y, -9);
      const d = box.max.z - box.min.z;
      const mesh = CreateBox(box.id, { width: w, height: h, depth: d, wrap: true }, scene);
      mesh.position.set((box.min.x + box.max.x) / 2, Math.max(box.min.y, -9) + h / 2, (box.min.z + box.max.z) / 2);
      // Each face of a box maps the texture once: tile it by the block's own size, in metres.
      const own = this.textured(scene, box.id, "rock", Math.max(w, d) / 2, h / 2);
      own.alpha = 1;
      this.occluders.push({ box, material: own });
      mesh.material = own;
      mesh.isPickable = false;
      this.receivers.push(mesh);
      if(box.kind==='platform'){
        const top=CreateGround(`${box.id}-walkable-top`,{width:w,height:d},scene);
        top.position.set((box.min.x+box.max.x)/2,box.max.y+.015,(box.min.z+box.max.z)/2);
        const cap=this.textured(scene,`${box.id}-cap`,'ash',w/2,d/2);
        top.material=cap;top.isPickable=false;this.receivers.push(top);
        this.occluders.push({box,material:cap});
        // Small rim fragments sit outside the landing corridor, never over it.
        for(let i=0;i<4;i++){
          const chip=CreateSphere(`${box.id}-rim`,{diameter:.32,segments:3},scene);
          chip.position.set(box.min.x+.3+i*(w-.6)/3,box.max.y+.04,box.max.z-.14);
          chip.scaling.set(1.5,.45,1);chip.material=own;chip.isPickable=false;
        }
        // Broken rock along the exposed wall feet softens the rectangular
        // shelves while keeping their entire landing surface unobstructed.
        for(let i=0;i<5;i++){
          const chip=CreateSphere(`${box.id}-talus`,{diameter:.65+(i%2)*.2,segments:3},scene);
          chip.position.set(box.min.x+.25+i*(w-.5)/4,Math.max(0,box.min.y)+.18,box.max.z+.12);
          chip.scaling.set(1.3,.6,1);chip.material=own;chip.isPickable=false;
        }
        continue;
      }
      if (box.id === "massif-back") continue;
      // A ragged crest of boulders, so it reads as a mountain and not a wall.
      for (let z = box.min.z + 0.8; z < box.max.z; z += 1.6 + random()) {
        const r = 1 + random() * 1.6;
        const rock = CreateSphere("massif-crest", { diameter: r * 2, segments: 3 }, scene);
        rock.position.set(box.max.x - 0.6 - random() * 2.5, box.max.y - 0.4 + random() * 0.8, z);
        rock.scaling.y = 0.6 + random() * 0.8;
        rock.material = material;
        rock.isPickable = false;
      }
    }
    // The way down: dark, lit from below.
    const back = boxes.find((b) => b.id === "massif-back");
    if (back) {
      const glow = new DynamicTexture("descent", { width: 16, height: 64 }, scene, false);
      const g = glow.getContext() as unknown as CanvasRenderingContext2D;
      const grad = g.createLinearGradient(0, 0, 0, 64);
      grad.addColorStop(0, "#050202");
      grad.addColorStop(0.55, "#2a0804");
      grad.addColorStop(1, "#ff5a14");
      g.fillStyle = grad;
      g.fillRect(0, 0, 16, 64);
      glow.update(true);
      const plane = CreatePlane("descent", { width: back.max.z - back.min.z, height: 7.3 }, scene);
      plane.position.set(back.max.x + 0.03, 3.65, (back.min.z + back.max.z) / 2);
      plane.rotation.y = -Math.PI / 2;
      const m = new StandardMaterial("descent", scene);
      m.disableLighting = true;
      m.emissiveTexture = glow;
      m.backFaceCulling = false;
      plane.material = m;
      this.occluders.push({ box: back, material: m });
      plane.isPickable = false;
      this.torches.push(new Vector3(back.max.x + 0.4, 0.6, (back.min.z + back.max.z) / 2));
    }
    return material;
  }

  private mesh(scene: Scene, name: string, data: VertexData, kind: SurfaceKind): Mesh {
    const mesh = new Mesh(name, scene);
    data.applyToMesh(mesh);
    mesh.material = this.textured(scene, name, kind, 1, 1);
    mesh.isPickable = false;
    mesh.freezeWorldMatrix();
    return mesh;
  }

  private textured(scene: Scene, name: string, kind: SurfaceKind, u: number, v: number): StandardMaterial {
    const material = new StandardMaterial(name, scene);
    const texture = surfaceTexture(scene, kind);
    texture.uScale = u * surfaceRepeat(kind);
    texture.vScale = v * surfaceRepeat(kind);
    material.diffuseTexture = texture;
    material.specularColor = Color3.Black();
    material.backFaceCulling = false;
    material.twoSidedLighting = true;
    material.maxSimultaneousLights = 8;
    if(kind==='rock'){
      const strata=new Texture('/proto2d/inferno-v2/cliff.png',scene,true,true,Texture.NEAREST_SAMPLINGMODE);
      strata.uScale=u/3;strata.vScale=v/2.5;
      material.diffuseTexture=strata;
      texture.dispose();
    }
    return material;
  }

  private buildEasternBoundary(scene:Scene):void {
    const continuation=CreateGround('land-beyond-stockade',{width:18,height:22},scene);
    continuation.position.set(27,-.08,0);continuation.material=this.textured(scene,'east-continuation','ash',4.5,5.5);
    continuation.isPickable=false;
    const texture=new Texture('/proto2d/inferno-v2/fence.png',scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    texture.hasAlpha=true;texture.wrapU=texture.wrapV=Texture.CLAMP_ADDRESSMODE;
    const material=new StandardMaterial('east-stockade',scene);
    material.diffuseTexture=texture;material.useAlphaFromDiffuseTexture=true;
    material.transparencyMode=StandardMaterial.MATERIAL_ALPHATEST;material.backFaceCulling=false;
    material.specularColor=Color3.Black();material.emissiveColor.set(.35,.28,.21);
    for(let z=-10;z<=10;z+=2.8){
      const fence=CreatePlane('stockade-section',{width:3.25,height:3.8},scene);
      fence.position.set(SHAPE.eastFenceX,1.7,z);fence.rotation.y=-Math.PI/2;
      fence.material=material;fence.isPickable=false;
    }
    const rockTexture=new Texture('/proto2d/inferno-rich/boulder-0.png',scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    rockTexture.hasAlpha=true;
    const rockMaterial=new StandardMaterial('shore-rocks',scene);
    rockMaterial.diffuseTexture=rockTexture;rockMaterial.useAlphaFromDiffuseTexture=true;rockMaterial.backFaceCulling=false;
    rockMaterial.transparencyMode=StandardMaterial.MATERIAL_ALPHATEST;rockMaterial.specularColor=Color3.Black();
    rockMaterial.emissiveColor.set(.25,.22,.18);
    for(let i=0;i<18;i++){
      const x=-14+i*2.1;
      const shore=x<0?SHAPE.b*(1-Math.abs(x/SHAPE.a)**SHAPE.p)**(1/SHAPE.p):SHAPE.b;
      const height=.8+(i%3)*.2;
      const rock=CreatePlane('shore-stone',{width:height*1.5,height},scene);
      rock.position.set(x,-.15+height/2,shore+.3+Math.sin(i*2)*.3);
      rock.billboardMode=Mesh.BILLBOARDMODE_Y;rock.material=rockMaterial;rock.isPickable=false;
    }
    for(let i=0;i<4;i++){
      const rock=CreatePlane('distant-eastern-rock',{width:3.8,height:3},scene);
      rock.position.set(23+i%2*3,1.2,-8+i*5);rock.billboardMode=Mesh.BILLBOARDMODE_Y;
      rock.material=rockMaterial;rock.isPickable=false;
    }
  }
}

// ---------------------------------------------------------------------------
// Geometry of the plateau (a rounded rectangle, |x/a|^p + |z/b|^p = 1)
// ---------------------------------------------------------------------------

function edge(t: number): { x: number; z: number } {
  return infernoEdge(t);
}

function plateauTop(): VertexData {
  const positions: number[] = [0, 0.002, 0];
  const uvs: number[] = [0, 0];
  const indices: number[] = [];
  const RINGS = 6;
  for (let s = 0; s < SEGMENTS; s++) {
    const e = edge((s / SEGMENTS) * Math.PI * 2);
    for (let r = 1; r <= RINGS; r++) {
      const f = r / RINGS;
      positions.push(e.x * f, 0.002, e.z * f);
      uvs.push((e.x * f) / ASH_TILE, (e.z * f) / ASH_TILE);
    }
  }
  const at = (s: number, r: number) => (r === 0 ? 0 : 1 + (s % SEGMENTS) * RINGS + (r - 1));
  for (let s = 0; s < SEGMENTS; s++) {
    for (let r = 0; r < RINGS; r++) {
      if (r === 0) indices.push(0, at(s, 1), at(s + 1, 1));
      else indices.push(at(s, r), at(s, r + 1), at(s + 1, r), at(s + 1, r), at(s, r + 1), at(s + 1, r + 1));
    }
  }
  return withNormals(positions, uvs, indices);
}

/** The cliff under the edge, slightly ragged so it does not look turned on a lathe. */
function plateauCliff(depth: number): VertexData {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  let u = 0;
  let prev = edge(0);
  for (let s = 0; s <= SEGMENTS; s++) {
    const e = edge((s / SEGMENTS) * Math.PI * 2);
    u += Math.hypot(e.x - prev.x, e.z - prev.z);
    prev = e;
    // Continuous terraces: never independent, needle-shaped bottom vertices.
    for(let row=0;row<4;row++){
      const factor=1+row*.055+Math.sin(s/SEGMENTS*Math.PI*6)*.008*row;
      positions.push(e.x*factor,-depth*row/2,e.z*factor);
      uvs.push(u/2,row*depth/4);
    }
  }
  for (let s = 0; s < SEGMENTS; s++) {
    const i = s * 4;
    for(let row=0;row<3;row++)indices.push(i+row,i+4+row,i+row+1,i+row+1,i+4+row,i+5+row);
  }
  return withNormals(positions, uvs, indices);
}

/** A sloping, irregular bank connects land depth to water depth gradually. */
function shoreBank():VertexData {
  const positions:number[]=[],uvs:number[]=[],indices:number[]=[];
  const samples=82;
  for(let i=0;i<=samples;i++){
    const x=-17+i*.5;
    const shore=x<0?SHAPE.b*Math.max(0,1-Math.abs(x/SHAPE.a)**SHAPE.p)**(1/SHAPE.p):SHAPE.b;
    for(let row=0;row<4;row++){
      positions.push(x,[-.04,-.45,-1.25,-2.43][row]!,shore+row+Math.sin(x*2.7)*.12);
      uvs.push(x/2,row*.5);
    }
  }
  for(let i=0;i<samples;i++)for(let row=0;row<3;row++){
    const k=i*4+row;indices.push(k,k+4,k+1,k+1,k+4,k+5);
  }
  return withNormals(positions,uvs,indices);
}

function withNormals(positions: number[], uvs: number[], indices: number[]): VertexData {
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.uvs = uvs;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  data.normals = normals;
  return data;
}
